import { supabase } from '../lib/supabaseClient'
import type { AdminStats, ProfessorStats, AlunoStats, Activity, RecentItem, ChartData } from '../types/dashboard.types'

export const dashboardService = {
    // ==================== ADMIN STATS ====================
    async getAdminStats(): Promise<AdminStats> {
        try {
            // Total de usuários
            const { count: totalUsers } = await supabase
                .from('profiles')
                .select('*', { count: 'exact', head: true })

            // Usuários por role
            const { data: profiles } = await supabase
                .from('profiles')
                .select('role') as any

            const totalAdmins = profiles?.filter((p: any) => p.role === 'admin').length || 0
            const totalProfessores = profiles?.filter((p: any) => p.role === 'professor').length || 0
            const totalAlunos = profiles?.filter((p: any) => p.role === 'aluno').length || 0

            // Total de aulas
            const { count: totalAulas } = await supabase
                .from('aulas')
                .select('*', { count: 'exact', head: true })

            // Total de testes
            const { count: totalTestes } = await supabase
                .from('testes')
                .select('*', { count: 'exact', head: true })

            // Total de questões
            const { count: totalQuestoes } = await supabase
                .from('questoes')
                .select('*', { count: 'exact', head: true })

            // Total de notícias
            const { count: totalNoticias } = await supabase
                .from('noticias')
                .select('*', { count: 'exact', head: true })

            // Notícias em destaque
            const { count: noticiasFeatured } = await supabase
                .from('noticias')
                .select('*', { count: 'exact', head: true })
                .eq('featured', true)

            // Testes realizados
            const { count: testesRealizados } = await supabase
                .from('resultados')
                .select('*', { count: 'exact', head: true })

            // Usuários ativos (últimos 30 dias)
            const thirtyDaysAgo = new Date()
            thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30)

            const { count: usuariosAtivos } = await supabase
                .from('profiles')
                .select('*', { count: 'exact', head: true })
                .gte('updated_at', thirtyDaysAgo.toISOString())

            // Crescimento mensal (novos usuários últimos 30 dias)
            const { count: crescimentoMensal } = await supabase
                .from('profiles')
                .select('*', { count: 'exact', head: true })
                .gte('created_at', thirtyDaysAgo.toISOString())

            // Taxa de conclusão média
            const { data: resultados } = await supabase
                .from('resultados')
                .select('completed_at')
                .not('completed_at', 'is', null)

            const taxaConclusaoMedia = resultados && testesRealizados
                ? (resultados.length / (testesRealizados || 1)) * 100
                : 0

            return {
                totalUsers: totalUsers || 0,
                totalAdmins,
                totalProfessores,
                totalAlunos,
                totalAulas: totalAulas || 0,
                totalTestes: totalTestes || 0,
                totalQuestoes: totalQuestoes || 0,
                totalNoticias: totalNoticias || 0,
                testesRealizados: testesRealizados || 0,
                taxaConclusaoMedia: Math.round(taxaConclusaoMedia),
                usuariosAtivos: usuariosAtivos || 0,
                crescimentoMensal: crescimentoMensal || 0,
                noticiasFeatured: noticiasFeatured || 0
            }
        } catch (error) {
            console.error('Erro ao buscar estatísticas admin:', error)
            throw error
        }
    },

    // ==================== PROFESSOR STATS ====================
    async getProfessorStats(professorId: string): Promise<ProfessorStats> {
        try {
            // Minhas aulas
            const { count: minhasAulas } = await supabase
                .from('aulas')
                .select('*', { count: 'exact', head: true })
                .eq('created_by', professorId)

            // Meus testes
            const { count: meusTestes } = await supabase
                .from('testes')
                .select('*', { count: 'exact', head: true })
                .eq('created_by', professorId)

            // Minhas questões
            const { count: minhasQuestoes } = await supabase
                .from('questoes')
                .select('*', { count: 'exact', head: true })
                .eq('created_by', professorId)

            // Testes realizados nos meus testes
            const { data: meusTestesData } = await supabase
                .from('testes')
                .select('id')
                .eq('created_by', professorId) as any

            const testeIds = meusTestesData?.map((t: any) => t.id) || []

            let testesRealizados = 0
            let alunosUnicos = 0
            let taxaAcertoMedia = 0

            if (testeIds.length > 0) {
                const { count } = await supabase
                    .from('resultados')
                    .select('*', { count: 'exact', head: true })
                    .in('teste_id', testeIds)

                testesRealizados = count || 0

                // Alunos únicos
                const { data: resultados } = await supabase
                    .from('resultados')
                    .select('aluno_id')
                    .in('teste_id', testeIds) as any

                const alunosSet = new Set(resultados?.map((r: any) => r.aluno_id))
                alunosUnicos = alunosSet.size

                // Taxa de acerto média
                const { data: resultadosCompletos } = await supabase
                    .from('resultados')
                    .select('percentual_acertos')
                    .in('teste_id', testeIds)
                    .not('percentual_acertos', 'is', null) as any

                if (resultadosCompletos && resultadosCompletos.length > 0) {
                    const soma = resultadosCompletos.reduce((acc: number, r: any) => acc + (r.percentual_acertos || 0), 0)
                    taxaAcertoMedia = Math.round(soma / resultadosCompletos.length)
                }
            }

            // Última aula criada
            const { data: ultimaAula } = (await supabase
                .from('aulas')
                .select('created_at')
                .eq('created_by', professorId)
                .order('created_at', { ascending: false })
                .limit(1)
                .single()) as { data: { created_at: string } | null }


            // Último teste criado
            const { data: ultimoTeste } = (await supabase
                .from('testes')
                .select('created_at')
                .eq('created_by', professorId)
                .order('created_at', { ascending: false })
                .limit(1)
                .single()) as { data: { created_at: string } | null }

            return {
                minhasAulas: minhasAulas || 0,
                meusTestes: meusTestes || 0,
                minhasQuestoes: minhasQuestoes || 0,
                alunosUnicos,
                testesRealizados,
                taxaAcertoMedia,
                ultimaAulaCriada: ultimaAula?.created_at,
                ultimoTesteCriado: ultimoTeste?.created_at
            }
        } catch (error) {
            console.error('Erro ao buscar estatísticas professor:', error)
            throw error
        }
    },

    // ==================== ALUNO STATS ====================
    async getAlunoStats(alunoId: string): Promise<AlunoStats> {
        try {
            // Aulas disponíveis (apenas das turmas do aluno)
            const { data: myAulas } = await supabase
                .from('turma_alunos')
                .select('turma:turmas(turma_aulas(aula_id))')
                .eq('aluno_id', alunoId) as any

            const aulaIds = new Set<string>()
            myAulas?.forEach((m: any) => m.turma?.turma_aulas?.forEach((ta: any) => aulaIds.add(ta.aula_id)))
            const aulasDisponiveis = aulaIds.size

            // Testes disponíveis (publicados e das turmas do aluno)
            const { data: myTestes } = await supabase
                .from('turma_alunos')
                .select('turma:turmas(turma_testes(teste_id))')
                .eq('aluno_id', alunoId) as any

            const testeIds = new Set<string>()
            myTestes?.forEach((m: any) => m.turma?.turma_testes?.forEach((tt: any) => testeIds.add(tt.teste_id)))
            const testesDisponiveis = testeIds.size

            // Meus testes realizados
            const { count: meusTestesRealizados } = await supabase
                .from('resultados')
                .select('*', { count: 'exact', head: true })
                .eq('aluno_id', alunoId)

            // Pontuação média
            const { data: meusResultados } = await supabase
                .from('resultados')
                .select('score, percentual_acertos, started_at, completed_at')
                .eq('aluno_id', alunoId)
                .not('completed_at', 'is', null) as any

            let pontuacaoMedia = 0
            let taxaAcerto = 0
            let tempoMedioPorTeste = 0
            let melhorPontuacao = 0

            if (meusResultados && meusResultados.length > 0) {
                // Pontuação média
                const somaScores = meusResultados.reduce((acc: number, r: any) => acc + (r.score || 0), 0)
                pontuacaoMedia = Math.round(somaScores / meusResultados.length)

                // Taxa de acerto
                const somaAcertos = meusResultados.reduce((acc: number, r: any) => acc + (r.percentual_acertos || 0), 0)
                taxaAcerto = Math.round(somaAcertos / meusResultados.length)

                // Melhor pontuação
                melhorPontuacao = Math.max(...meusResultados.map((r: any) => r.score || 0))

                // Tempo médio por teste (em minutos)
                const tempos = meusResultados
                    .filter((r: any) => r.started_at && r.completed_at)
                    .map((r: any) => {
                        const start = new Date(r.started_at!).getTime()
                        const end = new Date(r.completed_at!).getTime()
                        return (end - start) / 1000 / 60 // minutos
                    })

                if (tempos.length > 0) {
                    const somaTempos = tempos.reduce((acc: number, t: number) => acc + t, 0)
                    tempoMedioPorTeste = Math.round(somaTempos / tempos.length)
                }
            }

            // Testes em andamento
            const { count: testesEmAndamento } = await supabase
                .from('resultados')
                .select('*', { count: 'exact', head: true })
                .eq('aluno_id', alunoId)
                .is('completed_at', null)

            return {
                aulasDisponiveis: aulasDisponiveis || 0,
                testesDisponiveis: testesDisponiveis || 0,
                meusTestesRealizados: meusTestesRealizados || 0,
                pontuacaoMedia,
                taxaAcerto,
                tempoMedioPorTeste,
                melhorPontuacao,
                testesEmAndamento: testesEmAndamento || 0
            }
        } catch (error) {
            console.error('Erro ao buscar estatísticas aluno:', error)
            throw error
        }
    },

    // ==================== RECENT ACTIVITY ====================
    async getRecentActivity(role: string, _userId?: string): Promise<Activity[]> {
        const activities: Activity[] = []

        try {
            if (role === 'admin') {
                // Novos usuários
                const { data: newUsers } = await supabase
                    .from('profiles')
                    .select('full_name, created_at, role')
                    .order('created_at', { ascending: false })
                    .limit(5) as any

                newUsers?.forEach((user: any) => {
                    activities.push({
                        id: `user-${user.created_at}`,
                        type: 'user',
                        description: `Novo ${user.role}: ${user.full_name || 'Sem nome'}`,
                        timestamp: user.created_at,
                        icon: 'user-plus',
                        color: 'blue'
                    })
                })

                // Testes recentes
                const { data: recentTests } = await supabase
                    .from('resultados')
                    .select('id, created_at, score')
                    .order('created_at', { ascending: false })
                    .limit(5) as any

                recentTests?.forEach((test: any) => {
                    activities.push({
                        id: `test-${test.id}`,
                        type: 'test',
                        description: `Teste realizado - Pontuação: ${test.score}`,
                        timestamp: test.created_at,
                        icon: 'check-circle',
                        color: 'green'
                    })
                })
            }

            // Ordenar por timestamp
            activities.sort((a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime())

            return activities.slice(0, 10)
        } catch (error) {
            console.error('Erro ao buscar atividades:', error)
            return []
        }
    },

    // ==================== RECENT ITEMS ====================
    async getRecentItems(role: string, userId?: string): Promise<RecentItem[]> {
        const items: RecentItem[] = []

        try {
            if (role === 'aluno' && userId) {
                // Para alunos, buscar apenas itens das suas turmas
                const { data: myAulas } = await supabase
                    .from('turma_alunos')
                    .select('turma:turmas(turma_aulas(aula:aulas(id, title, description, created_at)))')
                    .eq('aluno_id', userId) as any

                myAulas?.forEach((m: any) => m.turma?.turma_aulas?.forEach((ta: any) => {
                    if (ta.aula) {
                        items.push({
                            id: ta.aula.id,
                            title: ta.aula.title,
                            description: ta.aula.description || undefined,
                            timestamp: ta.aula.created_at,
                            type: 'aula'
                        })
                    }
                }))

                const { data: myTestes } = await supabase
                    .from('turma_alunos')
                    .select('turma:turmas(turma_testes(teste:testes(id, title, description, created_at)))')
                    .eq('aluno_id', userId) as any

                myTestes?.forEach((m: any) => m.turma?.turma_testes?.forEach((tt: any) => {
                    if (tt.teste) {
                        items.push({
                            id: tt.teste.id,
                            title: tt.teste.title,
                            description: tt.teste.description || undefined,
                            timestamp: tt.teste.created_at,
                            type: 'teste'
                        })
                    }
                }))
            } else {
                // Admin e Professor veem global (ou professor vê os seus, mas manter atual por simplicidade se não for o foco)
                // Últimas aulas
                const { data: aulas } = await supabase
                    .from('aulas')
                    .select('id, title, description, created_at')
                    .order('created_at', { ascending: false })
                    .limit(5) as any

                aulas?.forEach((aula: any) => {
                    items.push({
                        id: aula.id,
                        title: aula.title,
                        description: aula.description || undefined,
                        timestamp: aula.created_at,
                        type: 'aula'
                    })
                })

                // Últimos testes
                const { data: testes } = await supabase
                    .from('testes')
                    .select('id, title, description, created_at')
                    .order('created_at', { ascending: false })
                    .limit(5) as any

                testes?.forEach((teste: any) => {
                    items.push({
                        id: teste.id,
                        title: teste.title,
                        description: teste.description || undefined,
                        timestamp: teste.created_at,
                        type: 'teste'
                    })
                })
            }

            // Ordenar por timestamp
            items.sort((a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime())

            return items.slice(0, 10)
        } catch (error) {
            console.error('Erro ao buscar itens recentes:', error)
            return []
        }
    },

    // ==================== CHART DATA ====================
    async getUserGrowthChart(): Promise<ChartData> {
        try {
            const { data: profiles } = await supabase
                .from('profiles')
                .select('created_at')
                .order('created_at', { ascending: true }) as any

            if (!profiles || profiles.length === 0) {
                return { labels: [], values: [] }
            }

            // Agrupar por mês
            const monthCounts: Record<string, number> = {}

            profiles.forEach((profile: any) => {
                const date = new Date(profile.created_at)
                const monthKey = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`
                monthCounts[monthKey] = (monthCounts[monthKey] || 0) + 1
            })

            const labels = Object.keys(monthCounts).slice(-6) // Últimos 6 meses
            const values = labels.map(label => monthCounts[label])

            return { labels, values }
        } catch (error) {
            console.error('Erro ao buscar dados de crescimento:', error)
            return { labels: [], values: [] }
        }
    }
}
